import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { RegionId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { useNow, useRegionId } from "store/selectors/hooks";
import { MyActivityItem, todayRange } from "./my-activity";
import { MyActivityRow, toMyActivityItem } from "./my-activity-row";

const arrivingTransition = {
    default: { type: "spring", stiffness: 500, damping: 30 },
    backgroundColor: { duration: 1.6, ease: "easeOut" },
} as const;

const arriving = {
    opacity: 0,
    y: -8,
    backgroundColor: "rgba(34, 197, 94, 0.18)",
};

const settled = {
    opacity: 1,
    y: 0,
    backgroundColor: "rgba(34, 197, 94, 0)",
};

const cardClasses = "overflow-hidden rounded-lg bg-white ring-1 ring-gray-300";

export const AgendaCompletedToday: React.FC<unknown> = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const input = { ...todayRange(now), regionId: regionId ?? ("" as RegionId) };
    const activityTypes = useActivityTypeOptions();

    const activityQuery = useQuery({
        ...trpcClient.touchpoint.myActivity.queryOptions(input),
        enabled: !!regionId,
    });

    const items: MyActivityItem[] = (activityQuery.data ?? []).map(toMyActivityItem);

    const onActivityTypeSaved = () =>
        queryClient.invalidateQueries({
            queryKey: trpcClient.touchpoint.myActivity.queryKey(),
        });

    if (!activityQuery.isSuccess) {
        return null;
    }

    return (
        <div className={items.length === 0 ? "hidden" : cardClasses}>
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <span className="h-5 w-1 flex-shrink-0 rounded-full bg-green-500" />
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
                <span className="text-sm font-semibold text-gray-800">
                    Completed today
                </span>
                <span className="text-xs text-gray-500">{items.length}</span>
            </div>
            <ul className="divide-y divide-gray-100">
                <AnimatePresence initial={false}>
                    {items.map(item => (
                        <motion.li
                            key={item.id}
                            layout
                            initial={arriving}
                            animate={settled}
                            transition={arrivingTransition}>
                            <MyActivityRow
                                item={item}
                                activityTypes={activityTypes}
                                onSaved={onActivityTypeSaved}
                            />
                        </motion.li>
                    ))}
                </AnimatePresence>
            </ul>
        </div>
    );
};
